const API_URL = process.env.REACT_APP_API_URL || 'https://unilinkapi-production.up.railway.app';

function urlBase64ToUint8Array(base64String) {
  const padding = '='.repeat((4 - (base64String.length % 4)) % 4);
  const base64 = (base64String + padding).replace(/-/g, '+').replace(/_/g, '/');
  const rawData = window.atob(base64);
  const outputArray = new Uint8Array(rawData.length);
  for (let i = 0; i < rawData.length; i += 1) {
    outputArray[i] = rawData.charCodeAt(i);
  }
  return outputArray;
}

function resolveAuthHeaders(authHeaders) {
  if (typeof authHeaders === 'function') return authHeaders() || {};
  if (authHeaders && typeof authHeaders === 'object') return authHeaders;
  return {};
}

function subscriptionToPayload(subscription) {
  if (!subscription) return null;
  const raw = typeof subscription.toJSON === 'function' ? subscription.toJSON() : subscription;
  return {
    endpoint: raw.endpoint,
    expirationTime: raw.expirationTime ?? null,
    keys: {
      p256dh: raw.keys?.p256dh,
      auth: raw.keys?.auth,
    },
  };
}

/** Call this synchronously inside a click handler (before any await). */
export function beginNotificationPermissionRequest() {
  if (typeof Notification === 'undefined') {
    return Promise.resolve('denied');
  }
  if (Notification.permission === 'granted' || Notification.permission === 'denied') {
    return Promise.resolve(Notification.permission);
  }
  try {
    return Notification.requestPermission();
  } catch (err) {
    console.warn('requestPermission failed', err);
    return Promise.resolve('denied');
  }
}

export async function registerServiceWorker() {
  if (!('serviceWorker' in navigator)) return null;
  try {
    const registration = await navigator.serviceWorker.register('/sw.js', { scope: '/' });
    await navigator.serviceWorker.ready;
    return registration;
  } catch (err) {
    console.warn('Service worker registration failed:', err);
    return null;
  }
}

/**
 * Ensure browser push subscription exists and is saved to the API.
 * Pass permissionPromise from beginNotificationPermissionRequest() when available.
 */
export async function subscribeAdminToPush(authHeaders, permissionPromise) {
  if (!('Notification' in window) || !('PushManager' in window) || !('serviceWorker' in navigator)) {
    return { ok: false, reason: 'unsupported' };
  }

  let permission = Notification.permission;
  if (permissionPromise) {
    try {
      permission = await permissionPromise;
    } catch {
      permission = Notification.permission;
    }
  } else if (permission === 'default') {
    // May be ignored by Chrome if not in a user-gesture stack
    permission = await Notification.requestPermission();
  }

  if (permission !== 'granted') {
    return { ok: false, reason: permission === 'denied' ? 'denied' : 'default' };
  }

  const registration = await registerServiceWorker();
  if (!registration) return { ok: false, reason: 'sw_failed' };

  const headers = resolveAuthHeaders(authHeaders);
  if (!headers.Authorization) {
    return { ok: false, reason: 'no_auth' };
  }

  const keyRes = await fetch(`${API_URL}/admin-alerts/vapid-public-key`, {
    headers: { ...headers },
  });
  const keyData = await keyRes.json().catch(() => ({}));
  if (!keyRes.ok || !keyData.publicKey) {
    console.warn('VAPID key fetch failed', keyRes.status, keyData);
    return { ok: false, reason: 'vapid', status: keyRes.status };
  }

  const appServerKey = urlBase64ToUint8Array(keyData.publicKey);
  let subscription = await registration.pushManager.getSubscription();

  // Re-subscribe if missing or if existing sub cannot be serialized with keys
  const existingPayload = subscriptionToPayload(subscription);
  if (!subscription || !existingPayload?.keys?.p256dh || !existingPayload?.keys?.auth) {
    if (subscription) {
      try {
        await subscription.unsubscribe();
      } catch {
        /* ignore */
      }
    }
    subscription = await registration.pushManager.subscribe({
      userVisibleOnly: true,
      applicationServerKey: appServerKey,
    });
  }

  const payload = subscriptionToPayload(subscription);
  if (!payload?.endpoint || !payload.keys?.p256dh || !payload.keys?.auth) {
    console.warn('Invalid push subscription payload', payload);
    return { ok: false, reason: 'invalid_subscription' };
  }

  const saveRes = await fetch(`${API_URL}/admin-alerts/push-subscribe`, {
    method: 'POST',
    headers: {
      'Content-Type': 'application/json',
      ...headers,
    },
    body: JSON.stringify({ subscription: payload }),
  });

  if (!saveRes.ok) {
    const errBody = await saveRes.json().catch(() => ({}));
    console.warn('Push subscribe save failed', saveRes.status, errBody);
    return { ok: false, reason: 'save_failed', status: saveRes.status, errBody };
  }

  try {
    localStorage.setItem('unilink_push_subscribed', '1');
  } catch {
    /* ignore */
  }

  return { ok: true, subscription: payload };
}

export function isPushPermissionGranted() {
  return typeof Notification !== 'undefined' && Notification.permission === 'granted';
}

export function getNotificationPermission() {
  if (typeof Notification === 'undefined') return 'unsupported';
  return Notification.permission;
}
